import axios from 'axios';
import debugService from './debugService';

// 🔄 RETRY CONFIGURATION for 429 Rate Limiting
const RETRY_CONFIG = {
  maxRetries: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds max
  backoffMultiplier: 2, // Exponential backoff
  retryableStatuses: [429, 503], // Rate limit and service unavailable
};

/**
 * 🔄 Exponential Backoff Calculator
 * Returns delay time based on retry attempt
 */
const calculateBackoffDelay = (retryCount) => {
  const delay = Math.min(
    RETRY_CONFIG.initialDelay * Math.pow(RETRY_CONFIG.backoffMultiplier, retryCount),
    RETRY_CONFIG.maxDelay
  );
  // Add jitter (±20%) to prevent thundering herd
  const jitter = delay * 0.2 * (Math.random() - 0.5);
  return Math.floor(delay + jitter);
};

/**
 * 🔄 Retry Wrapper with Exponential Backoff
 * Handles 429 errors gracefully without cascading to 401
 */
const retryWithBackoff = async (fn, retryCount = 0) => {
  try {
    return await fn();
  } catch (error) {
    const status = error.response?.status;
    const isRetryable = RETRY_CONFIG.retryableStatuses.includes(status);
    
    // Check if we should retry
    if (isRetryable && retryCount < RETRY_CONFIG.maxRetries) {
      const delay = calculateBackoffDelay(retryCount);
      const retryAfter = error.response?.headers['retry-after'];
      const actualDelay = retryAfter ? parseInt(retryAfter) * 1000 : delay;
      
      debugService.warn(
        `⏳ Retry ${retryCount + 1}/${RETRY_CONFIG.maxRetries} after ${actualDelay}ms (status: ${status})`
      );
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, actualDelay));
      
      // Recursive retry
      return retryWithBackoff(fn, retryCount + 1);
    }
    
    // Not retryable or max retries reached
    throw error;
  }
};

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: window.location.origin, // Use current domain
  timeout: 15000, // Default timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for authentication and logging
apiClient.interceptors.request.use(
  (config) => {
    // ✅ ADD: Authentication token to all requests
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    debugService.log(`🌐 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
      hasAuth: !!token,
      headers: config.headers
    });
    return config;
  },
  (error) => {
    debugService.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for logging and error handling
apiClient.interceptors.response.use(
  (response) => {
    debugService.log(`✅ API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url;
    debugService.error(`❌ API Error: ${status} ${url}`, error.response?.data);
    
    // 🔄 IMPORTANT: Don't logout on rate limiting (429) - it will be retried
    // Mark the error so we know it's been handled by interceptor
    error.handledByInterceptor = true;
    
    // ✅ Handle authentication errors (but NOT during rate limiting)
    if (status === 401) {
      const errorData = error.response.data;
      
      // Handle username change specifically
      if (errorData?.code === 'USERNAME_CHANGED') {
        // Clear token and redirect with special message
        localStorage.removeItem('token');
        localStorage.setItem('loginMessage', errorData.message || 'Username updated. Please log in with your new credentials.');
        window.location.href = '/login?reason=username_changed';
      } else {
        // Token expired or invalid (general case)
        debugService.error('🔒 Authentication failed - token expired or invalid');
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    // Handle specific error cases
    else if (status === 429) {
      debugService.warn('⚠️ Rate limited - will retry with backoff');
      // Don't logout - this will be handled by retry logic
    } else if (status === 500) {
      debugService.error('🚨 Server error - check backend logs');
    } else if (!error.response) {
      debugService.error('🔌 Network error - server unreachable');
    }
    
    return Promise.reject(error);
  }
);

// API methods object with retry logic (matches the expected interface)
const api = {
  // 🔄 Generic HTTP methods with automatic retry on 429/503
  get: (url, config = {}) => retryWithBackoff(() => apiClient.get(url, config)),
  post: (url, data = {}, config = {}) => retryWithBackoff(() => apiClient.post(url, data, config)),
  put: (url, data = {}, config = {}) => retryWithBackoff(() => apiClient.put(url, data, config)),
  delete: (url, config = {}) => retryWithBackoff(() => apiClient.delete(url, config)),
  patch: (url, data = {}, config = {}) => retryWithBackoff(() => apiClient.patch(url, data, config)),

  // Specific methods for SimpleLiveMonitoring.js compatibility
  getBotParticipants: (meetingId, options = {}) => {
    return retryWithBackoff(() => 
      apiClient.get(`/api/bot/meetings/${meetingId}/participants`, {
        timeout: options.timeout || 12000,
        ...options
      })
    );
  },

  getBotGroupStudents: (groupId, options = {}) => {
    return retryWithBackoff(() =>
      apiClient.get(`/api/bot/groups/${groupId}/students?role_filter=student&limit=1000`, {
        timeout: options.timeout || 10000,
        ...options
      })
    );
  },

  // Fallback APIs (to main server) - WITH /api/ PREFIX
  getLiveParticipants: (meetingId) => retryWithBackoff(() => 
    apiClient.get(`/api/meetings/${meetingId}/participants`)
  ),
  getGroupStudents: (groupId) => retryWithBackoff(() => 
    apiClient.get(`/api/groups/${groupId}/students?role_filter=student&limit=1000`)
  ),
  getLiveMeetings: () => retryWithBackoff(() => 
    apiClient.get('/api/meetings/live')
  ),

  // Additional methods that might be needed - WITH /api/ PREFIX
  smartSync: () => retryWithBackoff(() => apiClient.post('/api/sync/smart')),
  getMeetingDetails: (meetingId) => retryWithBackoff(() => 
    apiClient.get(`/api/meetings/${meetingId}`)
  ),
  getSystemHealth: () => retryWithBackoff(() => 
    apiClient.get('/api/system/health')
  ),
  
  // Bot system specific
  getBotHealth: () => retryWithBackoff(() => apiClient.get('/api/bot/health')),
  getBotStatus: () => retryWithBackoff(() => apiClient.get('/api/bot/status')),
};

export default api;

// Cache bust: DEBUG SYSTEM - API Service integrated with Debug Service. All API logging now respects Debug ON/OFF setting
